import { DataLayer } from '@piwikpro/react-piwik-pro';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';

import { Link, navigate } from 'gatsby';

import {
  Button,
  buttonClasses,
  Grid2 as Grid,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  BrochureDownloadItem,
  ConversionFormData,
  CRM_RequestType,
  Language,
  LeadIndustry,
  LeadIntent,
  Market,
  NavigationLinks,
  ServiceSegmentType,
} from '@mewa/types';
import {
  cssVars,
  extractTextMarking,
  markdownTextLinkStyle,
  ResponsiveGrid,
} from '@mewa/ui';

import { marketsConfig } from '../../../../../libs/config/markets';
import t from '../../../../../libs/translate';
import FriendlyCaptcha from '../../../../components/friendly-captcha/friendly-captcha';
import { ServiceSegmentLink } from '../../../../components/header/types';
import Img from '../../../../components/image';
import { CantoImage } from '../../../../components/strapi/canto-image';
import { isFeatureDev } from '../../../../helpers/feature-dev';
import { jpgPath } from '../../../../helpers/image-factory';
import { previousLocationWasExternal } from '../../../../helpers/previous-location-state';
import { BackButtonContainer, ButtonContainer, CaptchaError } from '../../base';
import { mobileImageHeight, mobileImageWidth } from '../../helper';
import { ConversionTranslations } from '../../translations-query';
import { FormState } from '../hooks/use-form-state';
import { persistFormState } from '../persistence';
import { FormCompleteStep } from './form-complete-step';
import { FormDataStep } from './form-data-step';

export const conversionIndustryKeys: Record<string, string> = {
  [LeadIndustry.healthcare]: 'lead_industry_healthcare',
  [LeadIndustry.craft]: 'lead_industry_craft',
  [LeadIndustry.industry]: 'lead_industry_industry',
  [LeadIndustry.publicInstitutions]: 'lead_industry_public_institutions',
  [LeadIndustry.serviceAndCommerce]: 'lead_industry_service_and_commerce',
};

export const Conversion = ({
  onSubmit,
  formState,
  formType,
  translations,
  market,
  lang,
  imageSrc,
  mobileImageSrc,
  formTitle,
  brochures,
  navigationLinks,
  trackingEvent,
  langPrefix,
  selectedStep,
  serviceSegmentLinks,
  thankYouImage,
}: {
  onSubmit: (formData: ConversionFormData) => Promise<void>;
  formState: FormState;
  formType: CRM_RequestType;
  translations: ConversionTranslations;
  market: Market;
  lang: Language;
  imageSrc: string;
  mobileImageSrc: string;
  formTitle: string;
  brochures?: BrochureDownloadItem[];
  navigationLinks: NavigationLinks;
  trackingEvent: string;
  langPrefix: string;
  selectedStep: number;
  serviceSegmentLinks: ServiceSegmentLink[];
  thankYouImage: CantoImage;
}) => {
  const theme = useTheme();

  const activeStep = selectedStep;
  const numberOfSteps = 2;
  const isLastStep = activeStep >= numberOfSteps - 1;

  useEffect(() => {
    // Setting the scrollRestoration to manual prevents a scroll position reset
    // when navigating via a hash URL. This is needed for the form to get a
    // smooth transition between the individual steps.
    window.history.scrollRestoration = 'manual';

    return () => {
      // By default, 'auto' is set as the `scrollRestoration` value, so we reset
      // the value when unmounting the form.
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  // Memoize image to prevent flickering image on re-render.
  const stickyImage = useMemo(() => {
    return (
      <ImageWrapper>
        <Img
          alt=""
          images={[
            {
              src: `${mobileImageSrc}`,
              media: `(max-width: ${theme.breakpoints.values.mdSm - 1}px)`,
              width: mobileImageWidth,
              height: mobileImageHeight,
            },
            {
              src: `${imageSrc}`,
              media: `(min-width: ${theme.breakpoints.values.mdSm}px)`,
              width: 960,
              height: 1181,
            },
          ]}
          style={{
            minHeight: '100%',
            objectFit: 'cover',
          }}
        />
      </ImageWrapper>
    );
  }, [imageSrc, mobileImageSrc, theme]);

  const handleSubmit = (formData: ConversionFormData) => {
    return onSubmit(formData);
  };

  return (
    <Grid
      container
      columns={{ xs: 1, mdSm: 2 }}
      direction={{ mdSm: 'row-reverse' }}
    >
      <Grid
        sx={{
          display: isLastStep ? 'none' : 'block',
        }}
        size={1}
      >
        {stickyImage}
      </Grid>
      <Grid size={{ xs: 1, mdSm: isLastStep ? 2 : 1 }}>
        <ResponsiveGrid>
          <Grid
            size={{
              xs: 12,
              mdSm:
                isLastStep && formType !== CRM_RequestType.OrderService
                  ? 6
                  : 12,
            }}
          >
            <ConversionForm
              formState={formState}
              formType={formType}
              changeStep={(newStep) => {
                // +1 so we don't show a 0 index in the URL
                window.location.hash = `${newStep + 1}`;
              }}
              numberOfSteps={numberOfSteps}
              userRequestedStep={activeStep}
              translations={translations}
              onSubmit={handleSubmit}
              market={market}
              lang={lang}
              langPrefix={langPrefix}
              formTitle={formTitle}
              brochures={brochures ?? []}
              navigationLinks={navigationLinks}
              trackingEvent={trackingEvent}
              serviceSegments={serviceSegmentLinks.map((s) => [s.key, s.name])}
            />
          </Grid>
          <Grid
            size={{ xs: 12, mdSm: 6 }}
            sx={{
              display:
                isLastStep && formType !== CRM_RequestType.OrderService
                  ? undefined
                  : 'none',
              marginBottom: '62px',
              marginTop: '40px',

              [theme.breakpoints.up('mdSm')]: {
                marginTop: '85px',
              },

              [theme.breakpoints.up('md')]: {
                marginTop: '176px',
              },
            }}
          >
            <Link
              to={`${langPrefix}/${navigationLinks.navigation.company.slug}`}
              className="link-container"
              style={{ color: 'var(--color-dark-coal)' }}
            >
              <Img
                images={[
                  {
                    src: jpgPath(
                      thankYouImage.cantoMobileImage ??
                        thankYouImage.cantoDesktopImage,
                      336 * 2,
                      252 * 2,
                    ),
                    media: `(max-width: ${theme.breakpoints.values.md - 1}px)`,
                    width: 336,
                    height: 252,
                  },
                  {
                    src: jpgPath(
                      thankYouImage.cantoMobileImage ??
                        thankYouImage.cantoDesktopImage,
                      844,
                      634,
                    ),
                    media: `(min-width: ${theme.breakpoints.values.md}px)`,
                    width: 844,
                    height: 634,
                  },
                ]}
                style={{
                  width: '100%',
                }}
                alt=""
              />
              <TeaserCaption>
                {t(translations, 'general_form_teaser_caption')}
              </TeaserCaption>
              <ResponsiveButton
                variant="text"
                color="secondary"
                sx={{
                  paddingTop: '0 !important',
                }}
              >
                {t(translations, 'general_form_teaser_button_label')}
              </ResponsiveButton>
            </Link>
          </Grid>
        </ResponsiveGrid>
      </Grid>
    </Grid>
  );
};

export const ResponsiveButton = styled(Button)(({ theme }) => ({
  [`&.${buttonClasses.sizeLarge}`]: {
    ...theme.typography.body,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const scrollToPosition = (
  offset: number,
  isMobile: boolean,
  elementTopPosition?: number,
) => {
  const headerHeight = parseInt(
    isMobile ? cssVars.headerHeightMobile : cssVars.headerHeightDesktop,
  );
  const actualScrollTop = document.documentElement.scrollTop;

  // for step changes and as default
  const stepStart = document.querySelector(`h1`);
  const topToScroll =
    elementTopPosition ?? stepStart?.getBoundingClientRect().top;

  if (topToScroll) {
    window.scrollTo({
      top: actualScrollTop + topToScroll - (headerHeight + offset),
      behavior: 'smooth',
    });
  }
};

const scrollToFirstError = (isMobile: boolean) => {
  setTimeout(() => {
    const offsetToHeader = 40;
    let element = document.querySelector('.Mui-error');
    let top = element?.getBoundingClientRect().top;

    // In some cases the element with the error class is not rendered
    // (e.g. mobile native dropdown) and the getBoundingClientRect().top is 0.
    // In these cases we want to go up in the DOM until we find an
    // element that is rendered.
    // Even if the element is a label element, we want to go up in the
    // DOM for a better positioning.
    while (
      (top === undefined || top === 0 || element?.nodeName === 'LABEL') &&
      !!element?.parentElement
    ) {
      element = element?.parentElement;
      top = element?.getBoundingClientRect().top;
    }

    scrollToPosition(offsetToHeader, isMobile, top);
  }, 0);
};

export const ConversionForm = ({
  formState,
  formType,
  userRequestedStep,
  numberOfSteps,
  changeStep,
  translations,
  market,
  lang,
  langPrefix,
  onSubmit,
  formTitle,
  brochures,
  navigationLinks,
  trackingEvent,
  serviceSegments,
}: {
  formState: FormState;
  formType: CRM_RequestType;
  userRequestedStep: number;
  numberOfSteps: number;
  translations: ConversionTranslations;
  market: Market;
  lang: Language;
  langPrefix: string;
  formTitle: string;
  brochures: BrochureDownloadItem[];
  navigationLinks: NavigationLinks;
  // Similar to React's setState function, changeStep (which is a callback)
  // receives a callback that determines a new step, given the current one.
  changeStep: (newStep: number) => void;
  onSubmit: (data: ConversionFormData) => Promise<void>;
  trackingEvent: string;
  serviceSegments: [ServiceSegmentType, string][];
}) => {
  const activeStep = userRequestedStep;
  const isMdAndUp = useMediaQuery(useTheme().breakpoints.up('md'));

  const { formDataState, isValid, triggerValidation } = formState;
  const [solution, setSolution] = useState<string>('');
  const [captchaValidation, setCaptchaValidation] = useState<string>('');
  const [captchaError, setCaptchaError] = useState(false);

  // Number used to track the form submission count to reset the recaptcha
  // after each submit.
  const [formKey, setFormKey] = useState(0);
  const [submitWasSuccessful, setSubmitWasSuccessful] = useState(true);

  const isLastStep = activeStep === numberOfSteps - 1;
  const showSendButton = activeStep === numberOfSteps - 2;

  useEffect(() => {
    if (captchaValidation === 'completed') {
      setCaptchaError(false);
    }
  }, [captchaValidation]);

  const submitForm = () => {
    DataLayer.push({
      event: trackingEvent,
    });

    const formData: ConversionFormData = {
      leadIntent: formDataState.leadIntent.value as LeadIntent,
      contact: {
        firstName: formDataState.firstName.value?.trim() ?? '',
        lastName: formDataState.lastName.value?.trim() ?? '',
        email: formDataState.email.value?.trim() ?? '',
        phone: formDataState.phone.value?.trim() ?? '',
        responsePreference: formDataState.responsePreference.value,
      },
      notes: formDataState.message.value?.trim() ?? '',
      serviceSegment: formDataState.serviceSegment.value,
      friendlyCaptchaSolution: solution,
    };

    // increment the form key to reset the friendly captcha widget and reset
    // the saved solution to disable the send button
    setFormKey((old) => old + 1);
    setSolution('');

    onSubmit(formData).catch(() => {
      setSubmitWasSuccessful(false);
    });
  };

  const nextStep = (e: FormEvent) => {
    e.preventDefault();

    triggerValidation();

    if (captchaValidation !== 'completed') {
      setCaptchaError(true);
    }

    if (isValid() && captchaValidation === 'completed') {
      persistFormState(formState);

      if (showSendButton) {
        submitForm();
      }

      changeStep(activeStep + 1);
    } else {
      scrollToFirstError(!isMdAndUp);
    }
  };

  const onBackClick = () => {
    persistFormState(formState);

    if (previousLocationWasExternal()) {
      navigate(`/${langPrefix}`);
    } else {
      navigate(-1);
    }
  };

  const jobApplyEmail = t(translations, 'contact_form_job_apply_hint_email');
  const jobApplyHintMarking = t(translations, 'contact_form_job_apply_hint', {
    email: `[${jobApplyEmail}]`,
  });
  const jobApplyHint = extractTextMarking(jobApplyHintMarking);

  return (
    <form onSubmit={nextStep}>
      <FormDataStep
        activeStep={activeStep}
        state={formDataState}
        translations={translations}
        market={market}
        formType={formType}
        phoneNumber={marketsConfig[market]?.countryCallingCode}
        privacyPolicyLink={`${langPrefix}/${navigationLinks.privacyPolicy.slug}`}
        serviceSegments={serviceSegments}
        formTitle={formTitle}
      />

      <FormCompleteStep
        activeStep={activeStep}
        translations={translations}
        brochures={brochures}
        langPrefix={langPrefix}
        selectedServiceSegment={
          formType === CRM_RequestType.ServiceTest
            ? navigationLinks.conversion.cleaningClothsServiceTestPage.name
            : (serviceSegments.find(
                ([key]) => key === formDataState.serviceSegment.value,
              )?.[1] ?? '')
        }
        submitWasSuccessful={submitWasSuccessful}
      />

      {!isLastStep && (
        <>
          <div>
            <FriendlyCaptcha
              onSolved={setSolution}
              resetKey={formKey}
              onStateChange={setCaptchaValidation}
            />
            {captchaError ? (
              <CaptchaError>
                {t(translations, 'general_form_captcha_error')}
              </CaptchaError>
            ) : null}
          </div>

          {(isFeatureDev || market === Market.IT) && jobApplyHint && (
            <JobApplyHint>
              {jobApplyHint.beforeMarking}
              <a href={`mailto:${jobApplyHint.marking}`}>
                {jobApplyHint.marking}
              </a>
              {jobApplyHint.afterMarking}
            </JobApplyHint>
          )}

          <ButtonContainer
            sx={{
              display: isLastStep ? 'none' : '',
            }}
          >
            <BackButtonContainer>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                onClick={onBackClick}
                sx={{ width: '100%' }}
              >
                {t(translations, 'general_form_button_label_back')}
              </Button>
            </BackButtonContainer>

            <Button
              sx={{ flexBasis: { xs: '100%', sm: 'auto' } }}
              type="submit"
              variant="contained"
              color="primary"
            >
              {showSendButton
                ? t(translations, 'general_form_button_label_submit')
                : t(translations, 'general_form_button_label_next')}
            </Button>
          </ButtonContainer>
        </>
      )}
    </form>
  );
};

const ImageWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('mdSm')]: {
    position: 'sticky',
    height: '100vh',
    top: 0,
    marginTop: 'calc(-1 * var(--header-height))',

    '.gatsby-image-wrapper': {
      height: '100vh',
    },
  },
}));

export const TeaserCaption = styled('h4')(({ theme }) => ({
  ...theme.typography.h4,
  fontWeight: theme.typography.fontWeightMedium,
  marginTop: '35px',
  marginBottom: '27px',
  width: '90%',
  [theme.breakpoints.down('sm')]: {
    ...theme.typography.h6,
    marginTop: '10px',
    marginBottom: '23px',
  },
}));

// const ButtonContainer = styled('div')(({ theme }) => ({
//   marginTop: '57px',
//   display: 'flex',
//   width: '100%',
//   flexDirection: 'column',
//   marginBottom: '100px',
//   gap: '50px',
//   justifyContent: 'flex-end',

//   [theme.breakpoints.up('sm')]: {
//     marginTop: '56px',
//     justifyContent: 'flex-end',
//     flexDirection: 'row',
//     marginBottom: '85px',
//     gap: '50px',
//   },
// }));

// const BackButtonWrapper = styled('div')(({ theme }) => ({
//   display: 'flex',
//   justifyContent: 'center',
//   flexDirection: 'column',
//   width: 'auto',
// }));

const JobApplyHint = styled('p')(({ theme }) => ({
  marginTop: 'var(--spacer-l)',
  ...theme.typography.footnote,

  '> a': {
    ...markdownTextLinkStyle,
    color: 'var(--color-dark-coal)',
    textDecorationColor: 'var(--color-dark-coal)',
  },
}));
